<template>
  <div class="agents-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Role Filter -->
        <div class="role-filter-container">
          <div class="action-buttons">
            <button @click="showAddAgentModal = true" class="add-agent-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add Agent
            </button>
            <button @click="showImportAgentsModal = true" class="import-agents-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
              Import Agents
            </button>
          </div>
          <select v-model="selectedRole" @change="loadAgents" class="role-filter">
            <option value="1">Customer Service Agents</option>
            <option value="2">Sales Agents</option>
            <option value="3">Marketing Agents</option>
          </select>
        </div>

        <!-- Stats Overview -->
        <div class="stats-overview">
          <div class="stat-card">
            <div class="stat-icon">👥</div>
            <div class="stat-content">
              <div class="stat-number">{{ agents.length }}</div>
              <div class="stat-label">Total {{ selectedRole === '1' ? 'Support' : selectedRole === '2' ? 'Sales' : 'Marketing' }} Agents</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">✅</div>
            <div class="stat-content">
              <div class="stat-number">{{ activeAgents }}</div>
              <div class="stat-label">Active Agents</div>
            </div>
          </div>
        </div>

        <!-- Agents Grid -->
        <div class="agents-grid">
          <div v-for="agent in agents" :key="agent.id" class="agent-card">
            <div class="agent-header">
              <div class="agent-info">
                <div class="agent-details">
                  <h3 class="agent-name">{{ agent.name }}</h3>
                  <div class="agent-meta">
                    <button
                      class="agent-code clickable"
                      @click="copyAgentCode(agent.agent_code)"
                      title="Click to copy"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                      </svg>
                      <span style="opacity: 0.7; margin-right: 4px;">Username:</span>{{ agent.agent_code }}
                    </button>
                    <span class="agent-department">{{ agent.department_name }}</span>
                    <span class="agent-status" :class="{ active: agent.active, inactive: !agent.active }">
                      {{ agent.active ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="agent-actions">
                <button @click="editAgent(agent)" class="action-btn edit-btn" title="Edit Agent">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                </button>
                <button @click="deleteAgent(agent)" class="action-btn delete-btn" title="Delete Agent">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Agent Permissions -->
            <div class="agent-permissions">
              <!-- Broadcast Permission Toggle (not for Marketing agents) -->
              <div v-if="selectedRole !== '3'" class="permission-row">
                <span class="permission-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 11v2h4v-2h-4zm-2 6.61c.96.71 2.21 1.65 3.2 2.39.4-.53.8-1.07 1.2-1.6-.99-.74-2.24-1.68-3.2-2.4-.4.54-.8 1.08-1.2 1.61zM20.4 5.6c-.4-.53-.8-1.07-1.2-1.6-.99.74-2.24 1.68-3.2 2.4.4.53.8 1.07 1.2 1.6.96-.72 2.21-1.65 3.2-2.4zM4 9c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1v4h2v-4h1l5 3V6L8 9H4zm11.5 3c0-1.33-.58-2.53-1.5-3.35v6.69c.92-.81 1.5-2.01 1.5-3.34z"/>
                  </svg>
                  Broadcast Access
                </span>
                <button
                  class="broadcast-toggle"
                  :class="{ active: !!agent.allow_broadcast && Number(agent.allow_broadcast) !== 0 }"
                  @click="toggleBroadcastPermission(agent)"
                  :disabled="togglingBroadcastId === agent.id"
                  :title="!!agent.allow_broadcast && Number(agent.allow_broadcast) !== 0 ? 'Revoke broadcast access' : 'Grant broadcast access'"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
              <!-- Show Customer Phone Toggle -->
              <div class="permission-row">
                <span class="permission-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                  Show Customer Phone
                </span>
                <button
                  class="broadcast-toggle"
                  :class="{ active: Number(agent.show_customer_phone) === 1 }"
                  @click="toggleShowCustomerPhone(agent)"
                  :disabled="togglingPhoneId === agent.id"
                  :title="Number(agent.show_customer_phone) === 1 ? 'Hide customer phone from this agent' : 'Allow agent to see customer phone'"
                >
                  <span class="toggle-knob"></span>
                </button>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="agent-card-footer">
              <button @click="setAgentPassword(agent)" class="set-password-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                Set Password
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading agents...</p>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && agents.length === 0" class="empty-state">
          <div class="empty-icon">👥</div>
          <h3>No agents found</h3>
          <p>No {{ selectedRole === '1' ? 'customer service' : selectedRole === '2' ? 'sales' : 'marketing' }} agents are currently available.</p>
        </div>

        <!-- QR Code Modal -->
        <QRCodeModal
          :visible="showQRModal"
          :agent-code="selectedAgentCode"
          @close="showQRModal = false"
        />

        <!-- Edit Agent Modal -->
        <EditAgentModal
          :visible="showEditModal"
          :agent="selectedAgent"
          :departments="departments"
          @close="showEditModal = false"
          @save="handleEditAgent"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmModal
          :visible="showDeleteModal"
          :agent="selectedAgent"
          @close="showDeleteModal = false"
          @confirm="handleDeleteAgent"
        />

        <!-- Add Agent Modal -->
        <AddAgentModal
          :visible="showAddAgentModal"
          :departments="departments"
          :error-message="addAgentError"
          @close="closeAddAgentModal"
          @save="handleAddAgent"
        />

        <!-- Set Password Modal -->
        <div v-if="showPasswordModal" class="modal-overlay" @click.self="showPasswordModal = false">
          <div class="modal-content password-modal">
            <div class="modal-header">
              <h2>Set Agent Password</h2>
              <button class="close-btn" @click="showPasswordModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="info-text">Set a password for <strong>{{ selectedAgent?.name }}</strong> ({{ selectedAgent?.agent_code }})</p>

              <div class="form-group">
                <label for="newPassword">New Password</label>
                <div class="password-input-wrapper">
                  <input
                    id="newPassword"
                    :type="showPasswordInput ? 'text' : 'password'"
                    v-model="newPassword"
                    placeholder="Enter password (min 8 characters)"
                    class="form-input"
                  />
                  <button
                    type="button"
                    class="toggle-password-btn"
                    @click="showPasswordInput = !showPasswordInput"
                  >
                    <svg v-if="!showPasswordInput" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  v-model="confirmPassword"
                  placeholder="Confirm password"
                  class="form-input"
                />
              </div>

              <div v-if="passwordError" class="error-message">
                {{ passwordError }}
              </div>

              <button
                class="generate-password-btn"
                @click="generateRandomPassword"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                Generate Random Password
              </button>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" @click="showPasswordModal = false">Cancel</button>
              <button class="btn btn-primary" @click="handleSetPassword" :disabled="settingPassword">
                {{ settingPassword ? 'Setting...' : 'Set Password' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Import Agents Modal -->
        <div v-if="showImportAgentsModal" class="modal-overlay" @click.self="showImportAgentsModal = false">
          <div class="modal-content import-modal">
            <div class="modal-header">
              <h2>Import Agents from Excel</h2>
              <button class="close-btn" @click="showImportAgentsModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="info-text">Upload an Excel file (.xlsx) with agent names and passwords</p>

              <div class="file-format-info">
                <strong>Excel Format:</strong>
                <ul>
                  <li>Column A: agent_name</li>
                  <li>Column B: password (min 8 characters)</li>
                  <li>Row 1: Headers (will be skipped)</li>
                  <li>Data starting from Row 2</li>
                </ul>
              </div>

              <div class="form-group">
                <label for="importDepartment">Department</label>
                <select id="importDepartment" v-model="importDepartmentId" class="form-input">
                  <option value="" disabled>Select Department</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label for="importRole">Agent Role</label>
                <select id="importRole" v-model="importAgentRoleId" class="form-input">
                  <option value="" disabled>Select Role</option>
                  <option value="1">Customer Service</option>
                  <option value="2">Sales</option>
                </select>
              </div>

              <div class="form-group">
                <label for="importFile">Excel File</label>
                <input
                  id="importFile"
                  type="file"
                  accept=".xlsx,.xls"
                  @change="handleFileSelect"
                  class="file-input"
                />
              </div>

              <div v-if="importError" class="error-message">
                {{ importError }}
              </div>

              <div v-if="importSuccess" class="success-message">
                <div class="success-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  {{ importSuccess.message }}
                </div>
                <div class="import-stats">
                  <p><strong>Imported:</strong> {{ importSuccess.imported_count }} agents</p>
                  <p v-if="importSuccess.failed_count > 0"><strong>Failed:</strong> {{ importSuccess.failed_count }}</p>
                  <p><strong>Total Agents:</strong> {{ importSuccess.total_agents_now }} / {{ importSuccess.agents_limit }}</p>
                </div>
                <a v-if="importSuccess.credentials_file_url" :href="importSuccess.credentials_file_url" target="_blank" class="download-link">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                  Download Credentials File
                </a>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" @click="closeImportModal">Cancel</button>
              <button class="btn btn-primary" @click="handleImportAgents" :disabled="!isImportReady || importingAgents">
                {{ importingAgents ? 'Importing...' : 'Import Agents' }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import QRCodeModal from '../components/QRCodeModal.vue'
import EditAgentModal from '../components/EditAgentModal.vue'
import DeleteConfirmModal from '../components/DeleteConfirmModal.vue'
import AddAgentModal from '../components/AddAgentModal.vue'
import { DashboardService } from '../services/dashboard'
import { toast } from '../utils/toast'

export default {
  name: 'AgentsView',
  components: {
    Sidebar,
    TopHeader,
    QRCodeModal,
    EditAgentModal,
    DeleteConfirmModal,
    AddAgentModal
  },
  data () {
    return {
      selectedRole: '1', // Default to Customer Service
      agents: [],
      departments: [],
      loading: false,
      // Modal states
      showQRModal: false,
      showEditModal: false,
      showDeleteModal: false,
      showAddAgentModal: false,
      showPasswordModal: false,
      showImportAgentsModal: false,
      selectedAgent: null,
      selectedAgentCode: '',
      addAgentError: '',
      // Password modal fields
      newPassword: '',
      confirmPassword: '',
      showPasswordInput: false,
      passwordError: '',
      settingPassword: false,
      // Import agents modal fields
      importDepartmentId: '',
      importAgentRoleId: '',
      importFile: null,
      importError: '',
      importSuccess: null,
      importingAgents: false,
      togglingBroadcastId: null,
      togglingPhoneId: null
    }
  },
  computed: {
    activeAgents () {
      return this.agents.filter(agent => agent.active).length
    },
    isImportReady () {
      return !!(this.importDepartmentId && this.importAgentRoleId && this.importFile)
    }
  },
  async mounted () {
    await Promise.all([
      this.loadAgents(),
      this.loadDepartments()
    ])
  },
  methods: {
    async loadAgents () {
      try {
        this.loading = true
        this.agents = await DashboardService.getAgents(parseInt(this.selectedRole))
      } catch (error) {
        console.error('Error loading agents:', error)
        this.agents = []
      } finally {
        this.loading = false
      }
    },

    async loadDepartments () {
      try {
        const result = await DashboardService.getDepartments()
        if (result.success) {
          this.departments = result.departments
        } else {
          console.error('Failed to load departments:', result.error)
          this.departments = []
        }
      } catch (error) {
        console.error('Error loading departments:', error)
        this.departments = []
      }
    },

    // Copy agent code to clipboard
    async copyAgentCode (agentCode) {
      try {
        await navigator.clipboard.writeText(agentCode)
        alert(`Agent username "${agentCode}" copied to clipboard!`)
      } catch (error) {
        console.error('Failed to copy agent code:', error)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = agentCode
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          alert(`Agent username "${agentCode}" copied to clipboard!`)
        } catch (err) {
          console.error('Fallback copy failed:', err)
          alert('Failed to copy agent username')
        }
        document.body.removeChild(textArea)
      }
    },

    // QR Code functionality
    showAgentQR (agent) {
      this.selectedAgentCode = agent.agent_code
      this.showQRModal = true
    },

    // Edit agent functionality
    editAgent (agent) {
      this.selectedAgent = { ...agent }
      this.showEditModal = true
    },

    async handleEditAgent (data) {
      try {
        const result = await DashboardService.editAgent(data.agentId, data.name, data.departmentId)

        if (result.success) {
          this.showEditModal = false
          await this.loadAgents() // Refresh agents list
          alert(result.message || 'Agent updated successfully')
        } else {
          throw new Error(result.error || 'Failed to update agent')
        }
      } catch (error) {
        console.error('Error updating agent:', error)
        alert('Failed to update agent: ' + error.message)
      }
    },

    // Delete agent functionality
    deleteAgent (agent) {
      this.selectedAgent = { ...agent }
      this.showDeleteModal = true
    },

    async handleDeleteAgent (agentId) {
      try {
        const result = await DashboardService.deleteAgent(agentId)

        if (result.success) {
          this.showDeleteModal = false
          await this.loadAgents() // Refresh agents list
          alert(result.message || 'Agent deleted successfully')
        } else {
          throw new Error(result.error || 'Failed to delete agent')
        }
      } catch (error) {
        console.error('Error deleting agent:', error)
        alert('Failed to delete agent: ' + error.message)
      }
    },

    // Close add agent modal and reset error
    closeAddAgentModal () {
      this.showAddAgentModal = false
      this.addAgentError = ''
    },

    // Add agent functionality
    async handleAddAgent (data) {
      // Clear previous errors
      this.addAgentError = ''

      try {
        const result = await DashboardService.addAgent(data.name, data.departmentId, data.agentRoleId)

        if (result.success) {
          this.showAddAgentModal = false
          await this.loadAgents() // Refresh agents list
          alert(result.message || 'Agent created successfully')
        } else {
          // Set error message to display in modal
          this.addAgentError = result.message || result.error || 'Failed to create agent'
        }
      } catch (error) {
        console.error('Error creating agent:', error)
        // Set error message to display in modal
        this.addAgentError = error.message || 'An error occurred while creating the agent'
      }
    },

    // Set password functionality
    setAgentPassword (agent) {
      this.selectedAgent = { ...agent }
      this.newPassword = ''
      this.confirmPassword = ''
      this.showPasswordInput = false
      this.passwordError = ''
      this.showPasswordModal = true
    },

    generateRandomPassword () {
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
      let password = ''
      for (let i = 0; i < 12; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length))
      }
      this.newPassword = password
      this.confirmPassword = password
      this.showPasswordInput = true
    },

    async handleSetPassword () {
      // Validate
      this.passwordError = ''

      if (!this.newPassword) {
        this.passwordError = 'Please enter a password'
        return
      }

      if (this.newPassword.length < 8) {
        this.passwordError = 'Password must be at least 8 characters'
        return
      }

      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = 'Passwords do not match'
        return
      }

      this.settingPassword = true

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/agents/set-password', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            agent_id: this.selectedAgent.id,
            password: this.newPassword
          })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Failed to set password')
        }

        if (data.success) {
          this.showPasswordModal = false
          alert(`Password set successfully for ${this.selectedAgent.name}!\n\nAgent Code: ${this.selectedAgent.agent_code}\nPassword: ${this.newPassword}\n\nPlease share these credentials with the agent securely.`)
        } else {
          throw new Error(data.message || 'Failed to set password')
        }
      } catch (error) {
        console.error('Error setting password:', error)
        this.passwordError = error.message
      } finally {
        this.settingPassword = false
      }
    },

    // Import agents functionality
    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file) {
        this.importFile = file
      }
    },

    closeImportModal () {
      this.showImportAgentsModal = false
      this.importDepartmentId = ''
      this.importAgentRoleId = ''
      this.importFile = null
      this.importError = ''
      this.importSuccess = null
    },

    async handleImportAgents () {
      // Validate inputs
      this.importError = ''
      this.importSuccess = null

      if (!this.importDepartmentId) {
        this.importError = 'Please select a department'
        return
      }

      if (!this.importAgentRoleId) {
        this.importError = 'Please select an agent role'
        return
      }

      if (!this.importFile) {
        this.importError = 'Please select an Excel file'
        return
      }

      this.importingAgents = true

      try {
        const token = localStorage.getItem('auth_token')
        const formData = new FormData()
        formData.append('file', this.importFile)
        formData.append('department_id', this.importDepartmentId)
        formData.append('agent_role_id', this.importAgentRoleId)

        const response = await fetch('https://api.quali-d.com/agents/import', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          body: formData
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Failed to import agents')
        }

        if (data.success) {
          this.importSuccess = data
          await this.loadAgents() // Refresh agents list

          // Reset file input
          const fileInput = document.getElementById('importFile')
          if (fileInput) {
            fileInput.value = ''
          }
          this.importFile = null
        } else {
          throw new Error(data.message || 'Failed to import agents')
        }
      } catch (error) {
        console.error('Error importing agents:', error)
        this.importError = error.message
      } finally {
        this.importingAgents = false
      }
    },

    // Toggle broadcast access per agent
    async toggleBroadcastPermission (agent) {
      this.togglingBroadcastId = agent.id
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/toggle-agent-permission', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ agent_id: agent.id })
        })
        const data = await response.json()
        if (data.success) {
          agent.allow_broadcast = data.allow_broadcast
          toast.success(data.allow_broadcast ? 'Broadcast access granted' : 'Broadcast access revoked')
        } else {
          toast.error(data.message || 'Failed to update broadcast permission')
        }
      } catch (error) {
        console.error('Error toggling broadcast permission:', error)
        toast.error('Failed to update broadcast permission')
      } finally {
        this.togglingBroadcastId = null
      }
    },

    // Toggle show_customer_phone per agent
    async toggleShowCustomerPhone (agent) {
      this.togglingPhoneId = agent.id
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/agents/toggle-show-customer-phone', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ agent_id: agent.id })
        })
        const data = await response.json()
        if (data.success) {
          agent.show_customer_phone = data.show_customer_phone
          toast.success(data.show_customer_phone ? 'Customer phone visible to agent' : 'Customer phone hidden from agent')
        } else {
          toast.error(data.message || 'Failed to update phone visibility')
        }
      } catch (error) {
        console.error('Error toggling phone visibility:', error)
        toast.error('Failed to update phone visibility')
      } finally {
        this.togglingPhoneId = null
      }
    }
  }
}
</script>

<style scoped>
.agents-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.role-filter-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.add-agent-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: #3b82f6;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-agent-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.role-filter {
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  min-width: 200px;
  transition: all 0.3s ease;
}

.role-filter:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.role-filter option {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.stats-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.stat-icon {
  font-size: 32px;
  opacity: 0.8;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
}

.agent-card {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  transition: all 0.2s;
}

.agent-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px var(--shadow-primary);
}

.agent-header {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.agent-meta {
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 12px;
}

.agent-code {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

.agent-department {
  color: var(--text-secondary);
}

.agent-status {
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.agent-status.active {
  background: rgba(22, 101, 52, 0.1);
  color: #16a34a;
}

.agent-status.inactive {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Dark mode specific styling for agent status */
.dark .agent-status.active {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

.dark .agent-status.inactive {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.agent-permissions {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--border-primary);
}

.permission-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
}

.permission-row + .permission-row {
  border-top: 1px solid var(--border-primary);
}

.permission-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.broadcast-toggle {
  position: relative;
  width: 40px;
  height: 22px;
  border: none;
  border-radius: 11px;
  background: var(--bg-secondary, #e2e8f0);
  cursor: pointer;
  transition: background 0.2s;
  padding: 0;
  outline: none;
  flex-shrink: 0;
}

.broadcast-toggle.active {
  background: #25d366;
}

.broadcast-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s;
  pointer-events: none;
}

.broadcast-toggle.active .toggle-knob {
  transform: translateX(18px);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    padding: 20px 16px;
  }

  .stats-overview {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .stat-card {
    padding: 16px;
    gap: 12px;
  }

  .stat-icon {
    font-size: 24px;
  }

  .stat-number {
    font-size: 20px;
  }

  .agents-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .agent-card {
    padding: 16px;
  }
}

/* Action buttons */
.agent-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.agent-card-footer {
  border-top: 1px solid var(--border-primary);
  padding-top: 10px;
  margin-top: 4px;
}

.set-password-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.set-password-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-color: var(--border-secondary);
}

.edit-btn:hover {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
}

.delete-btn {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Dark mode specific styling for action buttons */
.dark .edit-btn:hover {
  background: rgba(139, 92, 246, 0.2);
  color: #a855f7;
}

.dark .delete-btn {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.delete-btn:hover {
  background: #ef4444;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

/* Clickable agent code */
.agent-code.clickable {
  display: inline-flex;
  align-items: center;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  padding: 4px 10px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid var(--border-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.agent-code.clickable:hover {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.agent-code.clickable svg {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.agent-code.clickable:hover svg {
  opacity: 1;
}

@media (max-width: 768px) {
  .agent-actions {
    flex-direction: column;
    gap: 4px;
  }

  .action-btn {
    width: 32px;
    height: 32px;
  }
}

/* Password button styling */
.password-btn:hover {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.dark .password-btn:hover {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

/* Password Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.info-text {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.info-text strong {
  color: var(--text-primary);
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  padding-right: 48px;
}

.toggle-password-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password-btn:hover {
  color: var(--text-primary);
}

.error-message {
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  margin-bottom: 16px;
}

.generate-password-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.generate-password-btn:hover {
  background: var(--bg-tertiary);
  border-color: #3b82f6;
  color: #3b82f6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-primary);
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Import Agents Button and Modal Styles */
.action-buttons {
  display: flex;
  gap: 12px;
}

.import-agents-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: #10b981;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.import-agents-btn:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.file-format-info {
  background: var(--bg-secondary);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid var(--border-primary);
}

.file-format-info strong {
  display: block;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.file-format-info ul {
  margin: 8px 0 0 20px;
  padding: 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.file-format-info li {
  margin-bottom: 4px;
}

.file-input {
  width: 100%;
  padding: 12px;
  border: 2px dashed var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.file-input:hover {
  border-color: #3b82f6;
  background: var(--bg-tertiary);
}

.file-input::-webkit-file-upload-button {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #3b82f6;
  color: white;
  font-weight: 500;
  cursor: pointer;
  margin-right: 12px;
}

.file-input::-webkit-file-upload-button:hover {
  background: #2563eb;
}

.success-message {
  padding: 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  margin-bottom: 16px;
}

.success-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #10b981;
  font-weight: 600;
  margin-bottom: 12px;
}

.import-stats {
  color: var(--text-secondary);
  font-size: 14px;
  margin-bottom: 12px;
}

.import-stats p {
  margin: 4px 0;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #3b82f6;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;
}

.download-link:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}
</style>
